// src/components/modals/ForgotPasswordModal.js
import { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { XMarkIcon, KeyIcon } from '@heroicons/react/24/solid';
import Alert from '../common/Alert';
import ActionButton from '../common/ActionButton';
import FormField from '../common/FormField'; // Import the new FormField

const ForgotPasswordModal = ({ handleClose, style, onMouseDown }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Reset state when modal opens
    setEmail('');
    setMessage('');
    setMessageType('');
    setIsLoading(false);
  }, []);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address.');
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent! Check your inbox (and spam folder).');
      setMessageType('success');
    } catch (error) {
      let userMessage = 'An error occurred. Please try again.';
      if (error.code === 'auth/user-not-found') {
        userMessage = 'No account found with this email address.';
      }
      setMessage(userMessage);
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-md border border-border border-t-4 flex flex-col max-h-[90vh] sm:max-h-[80vh]"
      style={{
        borderTopColor: 'var(--color-primary)',
        ...style,
      }}
      onMouseDown={onMouseDown}
    >
      {/* Glassmorphic header */}
      <div
        className="relative p-4 sm:p-5 flex justify-between items-center overflow-hidden flex-shrink-0"
        style={{
          background: 'linear-gradient(135deg, var(--color-primary)15, var(--color-secondary)25)',
        }}
      >
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />

        {/* Content */}
        <div className="relative z-10 flex items-center">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
            style={{
              background: 'var(--color-primary)',
              boxShadow: '0 4px 12px var(--color-primary)30',
            }}
          >
            <KeyIcon className="h-5 w-5 text-white drop-shadow-sm" />
          </div>
          <h3 className="text-lg font-bold text-content-primary">Reset Password</h3>
        </div>

        <button onClick={handleClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handlePasswordReset} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {message && <Alert message={message} type={messageType} />}

          {/* Description */}
          <div
            className="relative p-4 rounded-2xl backdrop-blur-sm border-border overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, var(--color-accent)08, var(--color-accent)15)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />
            <div className="relative z-10">
              <p className="text-sm text-content-secondary leading-relaxed">
                Enter the email address associated with your account, and we&apos;ll send you a link to reset your password.
              </p>
            </div>
          </div>

          <FormField
            id="email"
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
            autoComplete="email"
          />
        </div>

        {/* Footer */}
        <div
          className="px-4 sm:px-6 py-3 sm:py-4 flex justify-end space-x-4 border-t border-border flex-shrink-0"
          style={{ background: 'var(--color-background-primary)' }}
        >
          <ActionButton variant="glassy" type="button" onClick={handleClose} color="neutral" className="w-full sm:w-auto px-4 py-2 text-white">
            Cancel
          </ActionButton>
          <ActionButton variant="glassy" type="submit" color="orange" className="w-full sm:w-auto px-4 py-2 text-white" loading={isLoading} disabled={!email || isLoading}>
            Send Reset Link
          </ActionButton>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordModal;